import React, { useCallback, useEffect, useRef, useState } from 'react';
import MathText from './MathText';
import MathAnswerInput from './MathAnswerInput';
import './weekly.css';

const API = 'https://api.brainteaserday.com/api/weekly';
export const weeklyDate = value => new Intl.DateTimeFormat('fr-FR', { timeZone: 'Europe/Paris', weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }).format(new Date(value));
export async function weeklyRequest(path, options = {}) {
  const response = await fetch(`${API}${path}`, { ...options, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('auth_token')}`, ...options.headers } });
  const data = await response.json();
  if (!response.ok) { const error = new Error(data.error || 'Impossible de charger le défi.'); error.data = data; throw error; }
  return data;
}

export default function WeeklyPage({ onBack }) {
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [answer, setAnswer] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const requests = useRef({ version: 0, submitting: false });
  const load = useCallback(async () => {
    if (requests.current.submitting) return;
    const version = ++requests.current.version;
    try { const data = await weeklyRequest(''); if (version === requests.current.version) { setChallenge(data.challenge); setError(''); } }
    catch (e) { if (version === requests.current.version) setError(e.message); }
    finally { if (version === requests.current.version) setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  // Refresh at release time even when this page has stayed open overnight.
  useEffect(() => {
    const refresh = () => { if (!document.hidden) load(); };
    const timer = setInterval(refresh, 30000);
    window.addEventListener('focus', refresh);
    return () => { clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, [load]);
  const active = challenge?.steps.find(s => s.status === 'open');
  useEffect(() => { setAnswer(''); }, [challenge?.id, active?.index]);
  async function submit(event) {
    event.preventDefault();
    if (!active || busy) return;
    requests.current.submitting = true; requests.current.version++;
    setBusy(true); setMessage('');
    try {
      const data = await weeklyRequest(`/${challenge.id}/answer`, { method: 'POST', body: JSON.stringify({ step: active.index, answer }) });
      setChallenge(data.challenge);
      setMessage(data.correct ? 'Bonne réponse ! Étape validée.' : 'Ce n’est pas encore ça. Tu peux réessayer.');
      if (data.correct) setAnswer('');
    } catch (e) { if (e.data?.challenge) setChallenge(e.data.challenge); setMessage(e.message); }
    finally { requests.current.submitting = false; setBusy(false); }
  }
  return <main className="page-container weekly-page">
    <button className="btn-link" onClick={onBack}>← Retour aux énigmes du jour</button>
    <header className="weekly-hero"><span className="weekly-eyebrow">LE GRAND DÉFI · LUNDI / MERCREDI / VENDREDI</span>
      <h1>Une semaine. Une enquête.</h1>
      <p>Résous chaque étape pour avancer dans le même grand problème. La suite se dévoile deux jours plus tard.</p>
    </header>
    {loading ? <p role="status">Chargement du défi…</p> : error ? <div role="alert" className="card weekly-card"><p>{error}</p><button className="btn" onClick={load}>Réessayer</button></div> : !challenge ? <section className="card weekly-card"><h2>Le prochain défi se prépare</h2><p>Reviens bientôt découvrir une nouvelle aventure en trois étapes.</p></section> : <>
      <section className="card weekly-card">
        <span className="weekly-eyebrow">{challenge.ended ? 'SEMAINE TERMINÉE' : challenge.upcoming ? 'À VENIR' : 'LE DÉFI DE LA SEMAINE'}</span>
        <h2>{challenge.title}</h2><p><MathText>{challenge.introduction}</MathText></p>
        <p className="weekly-muted">Du {weeklyDate(challenge.starts_at)} au {weeklyDate(challenge.ends_at)} (heure de Paris, clôture à cette heure).</p>
        <progress aria-label="Étapes résolues" max={challenge.steps.length} value={challenge.steps.filter(s => s.solved).length} />
        <p>{challenge.steps.filter(s => s.solved).length} / {challenge.steps.length} étapes résolues</p>
      </section>
      {challenge.completed && <section className="card weekly-card weekly-won" role="status"><span aria-hidden="true">🏅</span><h2>{challenge.badge_label}</h2><p>Enquête résolue ! Le badge de cette semaine est maintenant visible sur ton profil.</p></section>}
      {challenge.ended && !challenge.completed && <p>Le défi est clos. Tes étapes résolues restent visibles, mais le badge ne peut plus être gagné.</p>}
      {!active && <p role="status" aria-live="polite">{message}</p>}
      <div className="weekly-steps">{challenge.steps.map(step => <section className={`card weekly-card weekly-step ${step.status}`} key={step.index}>
        <div className="weekly-step-heading"><span className="weekly-number">{step.solved ? '✓' : step.index + 1}</span><div><h2>{step.title || `Étape ${step.index + 1}`}</h2><p className="weekly-muted">{weeklyDate(step.release_at)} · Paris</p></div></div>
        {step.question && <p><MathText>{step.question}</MathText></p>}
        {step.status === 'scheduled' && <p>Disponible à cette date, après avoir résolu les étapes précédentes.</p>}
        {step.status === 'locked' && <p>Résous l’étape précédente pour débloquer celle-ci.</p>}
        {step.status === 'closed' && <p>Cette étape n’a pas été résolue avant la clôture.</p>}
        {step.solved && <details><summary>Étape résolue · Voir l’explication</summary><p><MathText>{step.explanation}</MathText></p></details>}
        {step.status === 'open' && <form onSubmit={submit}><label htmlFor="weekly-answer">Ta réponse</label>
          {step.type === 'number' ? <MathAnswerInput id="weekly-answer" className="input" value={answer} onChange={e => setAnswer(e.target.value)} disabled={busy} required /> : <input id="weekly-answer" className="input" value={answer} onChange={e => setAnswer(e.target.value)} maxLength={128} disabled={busy} required />}
          <button className="btn btn-primary" disabled={busy || !answer.trim()}>{busy ? 'Vérification…' : 'Valider cette étape'}</button>
          <p role="status" aria-live="polite">{message}</p>
        </form>}
      </section>)}</div>
      {!challenge.completed && <aside className="weekly-reward"><span aria-hidden="true">🏅</span><div><strong>{challenge.badge_label}</strong><p>Résous les trois étapes avant la clôture pour ajouter ce badge à ta collection.</p></div></aside>}
    </>}
  </main>;
}

export function WeeklyBadges({ userId }) {
  const [badges, setBadges] = useState([]);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let alive = true; setBadges([]); setError(false);
    weeklyRequest(`/badges/${userId}`).then(data => { if (alive) setBadges(data.badges); }).catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, [userId, retry]);
  return <section className="card weekly-card"><h3>🏅 Les énigmes de la semaine</h3>
    {error ? <p>Badges indisponibles. <button className="btn-link" onClick={() => setRetry(v => v + 1)}>Réessayer</button></p> : badges.length ? <ul className="weekly-badges">{badges.map(b => <li key={b.id}><span aria-hidden="true">🏅</span><strong>{b.badge_label}</strong><span>{b.title}</span><small>Semaine du {new Date(b.starts_at).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' })}</small></li>)}</ul> : <p className="weekly-muted">Les badges des défis hebdomadaires résolus apparaîtront ici.</p>}
  </section>;
}
